import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Modal, Field, Select, TextInput, Textarea, StatusPill, useFetch } from "./ui";
import TicketPhotoGallery from "./TicketPhotoGallery";
import TicketActivityAttachments from "./TicketActivityAttachments";
import CompleteInstallationModal from "./CompleteInstallationModal";
import { Wrench, Clock, AlertTriangle, CheckCircle2, Circle, Loader2, X, Pencil, ChevronUp } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "on_hold", label: "On Hold" },
  { value: "resolution_support_required", label: "Resolution Support Required" },
  { value: "completed", label: "Completed" },
  { value: "closed", label: "Closed" },
];

const STATUS_TONE = {
  open: "info",
  in_progress: "warning",
  on_hold: "muted",
  resolution_support_required: "danger",
  completed: "success",
  closed: "success",
};

const ACT_STATUS_ICON = { pending: Circle, in_process: Loader2, completed: CheckCircle2, skipped: X };

function timeLeft(deadlineISO) {
  if (!deadlineISO) return { text: "—", overdue: false, urgent: false };
  const now = Date.now();
  const d = new Date(deadlineISO).getTime();
  const diff = d - now;
  const overdue = diff < 0;
  const hours = Math.abs(diff) / 3600000;
  if (overdue) {
    if (hours < 24) return { text: `${hours.toFixed(1)}h overdue`, overdue: true, urgent: true };
    return { text: `${(hours / 24).toFixed(1)}d overdue`, overdue: true, urgent: true };
  }
  if (hours < 24) return { text: `${hours.toFixed(1)}h left`, overdue: false, urgent: hours < 6 };
  return { text: `${(hours / 24).toFixed(1)}d left`, overdue: false, urgent: false };
}

/**
 * Shared Ticket Detail / Edit modal.
 * Used by:
 *   • Admin /crm/tickets worklist (row click)
 *   • Admin Opportunity Detail (click on completed ticket-trigger activity in Phase 4)
 *   • Service Team /service worklist (row click)
 *
 * Props:
 *   open: boolean
 *   ticketId: string — fetched fresh on open
 *   onClose: () => void
 *   onChanged: (updatedTicket) => void   // notify parent to reload list
 */
export default function TicketDetailModal({ open, ticketId, onClose, onChanged }) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const { data: technicians } = useFetch("/technicians");
  const { data: serviceTeam } = useFetch("/service-team/search");
  const [installModal, setInstallModal] = useState(null); // { activity }
  const [expandedId, setExpandedId] = useState(null);
  const [draftByActivity, setDraftByActivity] = useState({}); // { [aid]: { appointment_at, notes } }

  useEffect(() => {
    if (!open || !ticketId) { setTicket(null); return; }
    let alive = true;
    setLoading(true);
    api.get(`/service-tickets/${ticketId}`)
      .then((r) => { if (alive) setTicket(r.data); })
      .catch((err) => { toast.error(formatApiError(err.response?.data?.detail) || err.message); onClose?.(); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, ticketId, onClose]);

  async function updateStatus(newStatus) {
    try {
      const r = await api.put(`/service-tickets/${ticket.id}/status?status=${newStatus}`);
      setTicket(r.data);
      onChanged?.(r.data);
      toast.success(`Status → ${newStatus.replace(/_/g, " ")}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }

  async function assignTechnician(techId) {
    try {
      await api.put(`/service-tickets/${ticket.id}/assign?technician_id=${techId}`);
      const r = await api.get(`/service-tickets/${ticket.id}`);
      setTicket(r.data);
      onChanged?.(r.data);
      toast.success("Technician assigned");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }

  async function assignServiceIncharge(siUserId) {
    try {
      const r = await api.put(`/service-tickets/${ticket.id}/service-incharge?service_incharge_id=${siUserId}`);
      setTicket(r.data);
      onChanged?.(r.data);
      toast.success("Service Incharge reassigned");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }

  async function updateActivity(aid, patch) {
    const act = ticket.activities.find((a) => a.id === aid);
    // Intercept: closing the mirrored "Installation Completion confirmation" activity →
    // pull quote line items via the linked Opportunity and generate the Installed Base.
    if (patch.status === "completed" && act && act.status !== "completed" &&
        act.linked_opp_id && /completion confirmation/i.test(act.name)) {
      setInstallModal({ activity: act });
      return;
    }
    try {
      const r = await api.put(`/service-tickets/${ticket.id}/activities/${aid}`, patch);
      setTicket(r.data);
      onChanged?.(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }

  function toggleExpand(a) {
    if (expandedId === a.id) { setExpandedId(null); return; }
    setExpandedId(a.id);
    setDraftByActivity((prev) => ({ ...prev, [a.id]: { appointment_at: (a.appointment_at || "").slice(0, 10), notes: a.notes || "" } }));
  }

  async function saveActivityDraft(aid) {
    const draft = draftByActivity[aid] || {};
    await updateActivity(aid, {
      appointment_at: draft.appointment_at ? new Date(draft.appointment_at).toISOString() : null,
      notes: draft.notes || "",
    });
    toast.success("Activity updated");
  }

  async function reloadTicket() {
    const r = await api.get(`/service-tickets/${ticket.id}`);
    setTicket(r.data);
    onChanged?.(r.data);
  }

  if (!open) return null;

  const sla = ticket ? timeLeft(ticket.sla_deadline_at) : null;
  const isUrgent = ticket && (ticket.priority === "urgent" || sla?.overdue);
  const lead = ticket ? `${ticket.ticket_number} · ${ticket.title}` : "Ticket";

  return (
    <Modal open={open} onClose={onClose} title={lead} wide>
      {loading || !ticket ? (
        <div className="py-12 text-center font-mono text-xs uppercase tracking-widest text-[#94A3B8]" data-testid="ticket-detail-loading">
          Loading ticket…
        </div>
      ) : (
        <div className="space-y-5" data-testid={`ticket-detail-${ticket.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill value={ticket.type.replace("_", " ")} tone="info" />
            <StatusPill value={ticket.status.replace(/_/g, " ")} tone={STATUS_TONE[ticket.status] || "default"} />
            {isUrgent && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-red-600 text-white px-1.5 py-0.5">
                <AlertTriangle className="w-3 h-3" /> Urgent
              </span>
            )}
            <span className={"inline-flex items-center gap-1 text-[11px] font-mono " + (sla?.overdue ? "text-red-700 font-semibold" : sla?.urgent ? "text-amber-700" : "text-[#475569]")}>
              <Clock className="w-3 h-3" /> SLA: {sla?.text}
            </span>
            {ticket.warranty_status === "in_warranty" && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-emerald-100 text-emerald-800 px-2 py-0.5">
                In warranty {typeof ticket.warranty_days_left === "number" ? `· ${ticket.warranty_days_left}d left` : ""}
              </span>
            )}
            {ticket.is_billable && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-[#1F4E2C] text-white px-2 py-0.5">
                Bill the customer
              </span>
            )}
          </div>

          {/* Customer snapshot */}
          <div className="grid grid-cols-2 gap-3 bg-[#F4F6F1] p-4 border border-[#DDE5D8] text-xs font-mono text-[#0F1A12]">
            <div>
              <div className="text-[#94A3B8] uppercase tracking-widest text-[10px]">Customer</div>
              <div className="mt-0.5">{ticket.customer_snapshot?.customer_name || "—"}</div>
            </div>
            <div>
              <div className="text-[#94A3B8] uppercase tracking-widest text-[10px]">Phone</div>
              <div className="mt-0.5">{ticket.customer_snapshot?.phone || "—"}</div>
            </div>
            <div className="col-span-2">
              <div className="text-[#94A3B8] uppercase tracking-widest text-[10px]">Address</div>
              <div className="mt-0.5">{ticket.customer_snapshot?.address || "—"}</div>
            </div>
            {ticket.serial_code && (
              <div>
                <div className="text-[#94A3B8] uppercase tracking-widest text-[10px]">Serial</div>
                <div className="mt-0.5">{ticket.serial_code}</div>
              </div>
            )}
            {ticket.commissioning_date && (
              <div>
                <div className="text-[#94A3B8] uppercase tracking-widest text-[10px]">Commissioning</div>
                <div className="mt-0.5">{ticket.commissioning_date}</div>
              </div>
            )}
          </div>

          {/* Description */}
          {ticket.description && (
            <div className="text-xs text-[#475569] whitespace-pre-wrap border-l-2 border-[#DDE5D8] pl-3">
              {ticket.description}
            </div>
          )}

          {/* Status + Service Incharge + Assign technician */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Update Status">
              <Select value={ticket.status} onChange={(e) => updateStatus(e.target.value)} data-testid="ticket-detail-status">
                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Select>
            </Field>
            <Field label="Service Incharge (Franchisee / Manager)">
              <Select
                value={ticket.service_incharge_id || ""}
                onChange={(e) => e.target.value && assignServiceIncharge(e.target.value)}
                data-testid="ticket-detail-si"
              >
                <option value="">— Select Service Incharge —</option>
                {serviceTeam.map((sm) => (
                  <option key={sm.user_id} value={sm.user_id}>
                    {sm.label} · {sm.type === "service_franchisee" ? "Franchisee" : "Manager"}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Assign Technician (ticket level)">
              <Select value={ticket.technician_id || ""} onChange={(e) => e.target.value && assignTechnician(e.target.value)} data-testid="ticket-detail-tech">
                <option value="">— Select technician —</option>
                {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}{t.skill ? ` · ${t.skill}` : ""}</option>)}
              </Select>
            </Field>
          </div>

          {/* Activities (Phase 4 mirror) */}
          {ticket.activities?.length > 0 && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#1F4E2C] mb-2">
                Phase 4 Activities · {ticket.activities.filter((a) => a.status === "completed").length} / {ticket.activities.length} done
                <span className="text-[#94A3B8] normal-case tracking-normal ml-2">— updates sync back to the Opportunity in real time</span>
              </div>
              <div className="border border-[#DDE5D8] divide-y divide-[#EEF2EA]">
                {ticket.activities.map((a) => {
                  const Icon = ACT_STATUS_ICON[a.status] || Circle;
                  const isCompletionConfirmation = a.linked_opp_id && /completion confirmation/i.test(a.name);
                  const isExpanded = expandedId === a.id;
                  const draft = draftByActivity[a.id] || { appointment_at: "", notes: "" };
                  return (
                    <div key={a.id} data-testid={`ticket-detail-activity-${a.id}`}>
                      <div className="px-3 py-2.5 grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-4 flex items-center gap-2">
                          <Icon className={"w-4 h-4 flex-shrink-0 " +
                            (a.status === "completed" ? "text-emerald-600" :
                             a.status === "in_process" ? "text-amber-600 animate-spin" :
                             a.status === "skipped" ? "text-slate-400" : "text-[#94A3B8]")} />
                          <span className="text-xs text-[#0F1A12]">{a.name}</span>
                        </div>
                        <div className="col-span-3">
                          <Select value={a.status} onChange={(e) => updateActivity(a.id, { status: e.target.value })} data-testid={`ticket-detail-activity-status-${a.id}`}>
                            <option value="pending">Pending</option>
                            <option value="in_process">In Process</option>
                            <option value="completed">Completed</option>
                            <option value="skipped">Skipped</option>
                          </Select>
                        </div>
                        <div className="col-span-4">
                          <Select value={a.technician_id || ""} onChange={(e) => updateActivity(a.id, { technician_id: e.target.value })} data-testid={`ticket-detail-activity-tech-${a.id}`}>
                            <option value="">— Assign technician —</option>
                            {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </Select>
                        </div>
                        <div className="col-span-1 flex justify-end">
                          {isCompletionConfirmation ? (
                            <button type="button" onClick={() => setInstallModal({ activity: a })} title="Edit installation"
                              data-testid={`ticket-detail-activity-edit-install-${a.id}`}
                              className="text-[#1F4E2C]">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          ) : (
                            <button type="button" onClick={() => toggleExpand(a)} title="Edit date, notes & attachments"
                              data-testid={`ticket-detail-activity-edit-${a.id}`}
                              className="text-[#1F4E2C]">
                              {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
                            </button>
                          )}
                        </div>
                      </div>
                      {isExpanded && !isCompletionConfirmation && (
                        <div className="px-3 pb-3 pt-1 bg-[#FAFBF8] space-y-3" data-testid={`ticket-detail-activity-panel-${a.id}`}>
                          <div className="grid md:grid-cols-2 gap-3">
                            <Field label="Date">
                              <TextInput type="date" value={draft.appointment_at}
                                onChange={(e) => setDraftByActivity((prev) => ({ ...prev, [a.id]: { ...prev[a.id], appointment_at: e.target.value } }))}
                                data-testid={`ticket-detail-activity-date-${a.id}`} />
                            </Field>
                            <Field label="Notes">
                              <Textarea value={draft.notes} rows={2}
                                onChange={(e) => setDraftByActivity((prev) => ({ ...prev, [a.id]: { ...prev[a.id], notes: e.target.value } }))}
                                data-testid={`ticket-detail-activity-notes-${a.id}`} />
                            </Field>
                          </div>
                          <TicketActivityAttachments ticketId={ticket.id} activityId={a.id} />
                          <div className="flex justify-end">
                            <button type="button" onClick={() => saveActivityDraft(a.id)}
                              data-testid={`ticket-detail-activity-save-${a.id}`}
                              className="text-xs font-mono uppercase tracking-widest text-white bg-[#1F4E2C] px-3 py-1.5">
                              Save
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {ticket.linked_opp_id && (
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#1F4E2C] border-t border-[#DDE5D8] pt-3">
              <Wrench className="w-3 h-3" /> Linked to Opportunity · syncs back to Phase 4 activities
            </div>
          )}

          {/* Commissioning photos — uploaded by technicians / SI / admin; surface on the Opportunity's Installation card */}
          <div className="border-t border-[#DDE5D8] pt-3">
            <TicketPhotoGallery ticketId={ticket.id} canUpload canDelete />
          </div>
        </div>
      )}
      <CompleteInstallationModal
        open={!!installModal}
        oid={installModal?.activity?.linked_opp_id}
        activityId={installModal?.activity?.linked_opp_activity_id}
        customerName={ticket?.customer_snapshot?.customer_name}
        onClose={() => setInstallModal(null)}
        onSuccess={() => { setInstallModal(null); reloadTicket(); }}
      />
    </Modal>
  );
}
